import React from 'react';

import {assert} from 'Asserts';
import {NucleotideType} from 'biojs/BioConstants';
import {PolymerSubtype} from 'constants/MolbioConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {withoutUnresolvedAaSequenceNode} from 'graphql-client/WithoutUnresolvedAaSequenceNode';
import {
  BiointelligentLinkContainerCountQuery,
  BiointelligentLinkContainerCountQueryVariables,
} from 'molbio/biointelligent-links/__generated/BiointelligentLinkContainerCountQuery';
import {
  BiointelligentLinkType,
  LINK_TYPE_TO_COMPONENT_NAME,
} from 'molbio/biointelligent-links/BiointelligentLinkConstants';
import {BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY} from 'molbio/biointelligent-links/BiointelligentLinkQueries';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {getSum} from 'util/numbers/getSum';

export interface BiointelligentLinkComponentInfoBannerProps {
  numReferencedPolymersByLinkType: ReferencedPolymerCountsByComponentLinkType;
  polymerId: string;
  onDuplicatePolymer: () => Promise<string | null>;
}

type DnaComponentLinkTypes = BiointelligentLinkType.DNA_PART;
type RnaComponentLinkTypes = BiointelligentLinkType.RNA_PART | BiointelligentLinkType.TRANSCRIPTION;
type AaComponentLinkTypes = BiointelligentLinkType.AMINO_ACID_PART | BiointelligentLinkType.TRANSLATION;

export type ReferencedPolymerCountsByComponentLinkType =
  | {type: PolymerSubtype.dna_sequence; references: {[linkType in DnaComponentLinkTypes]: number}}
  | {type: PolymerSubtype.rna_sequence; references: {[linkType in RnaComponentLinkTypes]: number}}
  | {type: PolymerSubtype.aa_sequence; references: {[linkType in AaComponentLinkTypes]: number}};

export function getLinkContainersSummary(
  numReferencedPolymersByLinkType: ReferencedPolymerCountsByComponentLinkType
): string {
  const references = numReferencedPolymersByLinkType.references;
  const nonZeroReferencedLinkTypes = Object.keys(references).filter(
    (linkType: keyof typeof references) => references[linkType] > 0
  );
  assert(
    nonZeroReferencedLinkTypes.length <= 2,
    'Component unexpectedly participates in too many biointelligent link types.'
  );
  return nonZeroReferencedLinkTypes
    .map(
      (linkType: keyof typeof references) =>
        `a ${LINK_TYPE_TO_COMPONENT_NAME[linkType]} in ${references[linkType]} containing sequences`
    )
    .join(' and ');
}

function BiointelligentLinkComponentInfoBanner({
  numReferencedPolymersByLinkType,
  onDuplicatePolymer,
}: BiointelligentLinkComponentInfoBannerProps): JSX.Element {
  if (getSum(Object.values(numReferencedPolymersByLinkType.references)) === 0) {
    return <div />;
  }
  const message = `This sequence is referenced as ${getLinkContainersSummary(numReferencedPolymersByLinkType)}. Edit residues by creating a copy of this sequence.`;
  return (
    <InlineBanner
      dataTestAttrs={{
        component: 'BiointelligentLinkComponentInfoBanner',
        element: 'root',
      }}
      bannerType={InlineBannerType.INFO}
      text={message}
      supportingInfo="Optionally update existing links after registering the new copy."
      action={{
        actionType: InlineBannerActionType.ACCORDION_AND_CUSTOM,
        actionText: 'Create copy',
        actionOnClick: onDuplicatePolymer,
        rightAlignActionButton: true,
        buttonStyle: ButtonStyle.PRIMARY,
      }}
    />
  );
}

export default function QueryingBiointelligentLinkComponentInfoBanner(
  props: Omit<BiointelligentLinkComponentInfoBannerProps, 'numReferencedPolymersByLinkType'>
): JSX.Element {
  const queryResult = useBenchlingQuery<
    BiointelligentLinkContainerCountQuery,
    BiointelligentLinkContainerCountQueryVariables
  >(BIOINTELLIGENT_LINK_CONTAINER_COUNT_QUERY, {variables: {id: props.polymerId}});
  if (queryResult.type !== QueryResultType.SUCCESS) {
    return <></>;
  }
  const polymer = withoutUnresolvedAaSequenceNode(queryResult.data.polymer);
  let numReferencedPolymersByLinkType: ReferencedPolymerCountsByComponentLinkType;
  if (polymer.__typename === 'InaccessibleInfoNode') {
    assert(
      false,
      'Unexpectedly tried to render BiointelligentLinkComponentInfoBanner for inaccessible sequence'
    );
    return <></>;
  } else if (polymer.__typename === 'ProteinNode') {
    numReferencedPolymersByLinkType = {
      type: PolymerSubtype.aa_sequence,
      references: {
        [BiointelligentLinkType.AMINO_ACID_PART]: polymer.partContainerCount,
        [BiointelligentLinkType.TRANSLATION]: polymer.translationContainerCount,
      },
    };
  } else if (polymer.nucleotideType === NucleotideType.DNA) {
    numReferencedPolymersByLinkType = {
      type: PolymerSubtype.dna_sequence,
      references: {
        [BiointelligentLinkType.DNA_PART]: polymer.partContainerCount,
      },
    };
  } else {
    numReferencedPolymersByLinkType = {
      type: PolymerSubtype.rna_sequence,
      references: {
        [BiointelligentLinkType.RNA_PART]: polymer.partContainerCount,
        [BiointelligentLinkType.TRANSCRIPTION]: polymer.transcriptionContainerCount,
      },
    };
  }

  return (
    <BiointelligentLinkComponentInfoBanner
      {...props}
      numReferencedPolymersByLinkType={numReferencedPolymersByLinkType}
    />
  );
}
